// The documents view's Page view (design_handoff_documents_view/screens/Document Changes.dc.html): a Markdown document
// before and after, as one page with the changes marked. Plain module, no React and no HTML: the page component renders
// the blocks and their text runs as React text, so nothing in a document (agent output, untrusted) becomes markup.
//
// redline(oldMd, newMd) -> blocks [{type, change, segs, level?, ordered?, header?}]
//   type: h1..h6, p, li, quote, code, tr (a table row, its cells split by " | "), hr
//   change: same, add, del or mod; segs: [{t: text, k: same|add|del, s: ""|b|i|code|link}]
// changesOnly(blocks) -> the changed blocks, the heading above each for context ({..., context: true}), and
//   {gap: n} for each run of n unchanged blocks left out

// ---- a longest common subsequence, as edit operations; past MAX cells it gives up and replaces the whole run ----
const MAX = 4_000_000;
export function lcs(a, b, eq) {
  const n = a.length, m = b.length;
  if (n * m > MAX) return [...a.map((x) => ["-", x, null]), ...b.map((y) => ["+", null, y])];
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = eq(a[i], b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const ops = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (eq(a[i], b[j])) { ops.push(["=", a[i], b[j]]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push(["-", a[i], null]); i++; }
    else { ops.push(["+", null, b[j]]); j++; }
  }
  while (i < n) ops.push(["-", a[i++], null]);
  while (j < m) ops.push(["+", null, b[j++]]);
  return ops;
}

// ---- Markdown blocks: the structure that matters on a page; anything else is a paragraph ----
const FENCE = /^\s*(```|~~~)/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const cells = (line) => line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()).join(" | ");

export function mdBlocks(md) {
  const out = [];
  if (md == null) return out;
  const lines = String(md).replace(/\r\n?/g, "\n").split("\n");
  let para = null;
  const flush = () => { if (para) { out.push({ type: "p", text: para.join(" ") }); para = null; } };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (FENCE.test(line)) {
      flush();
      const close = line.trim().slice(0, 3), body = [];
      for (i++; i < lines.length && !lines[i].trim().startsWith(close); i++) body.push(lines[i]);
      out.push({ type: "code", text: body.join("\n") });
      continue;
    }
    if (!line.trim()) { flush(); continue; }
    let m;
    if ((m = HEADING.exec(line))) { flush(); out.push({ type: `h${m[1].length}`, text: m[2] }); continue; }
    if (RULE.test(line)) { flush(); out.push({ type: "hr", text: "" }); continue; }
    if (line.includes("|") && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      flush();
      out.push({ type: "tr", text: cells(line), header: true });
      for (i += 2; i < lines.length && lines[i].includes("|") && lines[i].trim(); i++) out.push({ type: "tr", text: cells(lines[i]) });
      i--;
      continue;
    }
    if ((m = ITEM.exec(line))) {
      flush();
      const item = { type: "li", text: m[3], ordered: /\d/.test(m[2]), marker: m[2], level: Math.min(4, Math.floor(m[1].replace(/\t/g, "    ").length / 2)) };
      // its continuation lines: indented, not a new item
      while (i + 1 < lines.length && /^\s{2,}\S/.test(lines[i + 1]) && !ITEM.test(lines[i + 1])) item.text += ` ${lines[++i].trim()}`;
      out.push(item);
      continue;
    }
    if ((m = QUOTE.exec(line))) {
      flush();
      const body = [m[1]];
      while (i + 1 < lines.length && QUOTE.test(lines[i + 1])) body.push(QUOTE.exec(lines[++i])[1]);
      out.push({ type: "quote", text: body.join(" ").trim() });
      continue;
    }
    (para ||= []).push(line.trim());
  }
  flush();
  return out;
}

// ---- inline: **bold**, __bold__, *italic*, _italic_, `code`, [label](target) as styled runs of plain text ----
const INLINE = /\\([\\`*_[\]()#+\-.!|])|(\*\*|__)(?=\S)(.+?)(?<=\S)\2|(\*|_)(?=\S)(.+?)(?<=\S)\4(?![\w*])|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
export function inlineRuns(text) {
  const runs = [];
  const add = (t, s) => { if (!t) return; const last = runs[runs.length - 1]; if (last && last.s === s) last.t += t; else runs.push({ t, s }); };
  let at = 0, m;
  const src = String(text || "");
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(src))) {
    add(src.slice(at, m.index), "");
    if (m[1]) add(m[1], "");
    else if (m[3] !== undefined) add(m[3], "b");
    else if (m[5] !== undefined) add(m[5], "i");
    else if (m[6] !== undefined) add(m[6], "code");
    else add(m[7], "link");
    at = INLINE.lastIndex;
  }
  add(src.slice(at), "");
  return runs;
}

// ---- words: letters and digits together, every space run and punctuation mark on its own ----
const TOKEN = /\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu;
const tokensOf = (s) => s.match(TOKEN) || [];

function styledDiff(oldText, newText) {
  const or = inlineRuns(oldText), nr = inlineRuns(newText);
  const style = (runs) => runs.flatMap((r) => Array.from(r.t, () => r.s));
  const op = or.map((r) => r.t).join(""), np = nr.map((r) => r.t).join("");
  const os = style(or), ns = style(nr);
  const raw = [];
  let po = 0, pn = 0, same = 0;
  const push = (t, k, s) => { const last = raw[raw.length - 1]; if (last && last.k === k && last.s === s) last.t += t; else raw.push({ t, k, s }); };
  for (const [kind, x, y] of lcs(tokensOf(op), tokensOf(np), (p, q) => p === q)) {
    if (kind === "=") { for (const ch of y) push(ch, "same", ns[pn++] || ""); po += Array.from(x).length; same += x.length; }
    else if (kind === "-") { for (const ch of x) push(ch, "del", os[po++] || ""); }
    else { for (const ch of y) push(ch, "add", ns[pn++] || ""); }
  }
  // a change made of several words reads as one: what was there, struck, then what's there now
  const segs = [];
  for (let i = 0; i < raw.length;) {
    if (raw[i].k === "same") { segs.push(raw[i]); i++; continue; }
    const dels = [], adds = [];
    let j = i;
    for (; j < raw.length; j++) {
      const r = raw[j];
      if (r.k === "del") dels.push(r);
      else if (r.k === "add") adds.push(r);
      else if (/^\s+$/.test(r.t) && j + 1 < raw.length && raw[j + 1].k !== "same") { dels.push({ ...r, k: "del" }); adds.push({ ...r, k: "add" }); }
      else break;
    }
    segs.push(...dels, ...adds);
    i = j;
  }
  // neighbours of the same kind and style are one mark
  const merged = [];
  for (const s of segs) { const last = merged[merged.length - 1]; if (last && last.k === s.k && last.s === s.s) last.t += s.t; else merged.push({ ...s }); }
  return { segs: merged, ratio: same / Math.max(op.length, np.length, 1) };
}

const plain = (b, k) => (b.type === "code" ? [{ t: b.text, k, s: "code" }] : b.type === "hr" ? [] : inlineRuns(b.text).map((r) => ({ ...r, k })));

export function redline(oldMd, newMd) {
  const A = mdBlocks(oldMd), B = mdBlocks(newMd), out = [];
  const ops = lcs(A, B, (x, y) => x.type === y.type && x.text === y.text);
  const keep = (b, change, segs) => out.push({ ...b, change, segs });
  for (let k = 0; k < ops.length; k++) {
    if (ops[k][0] === "=") { keep(ops[k][2], "same", plain(ops[k][2], "same")); continue; }
    const dels = [], adds = [];
    for (; k < ops.length && ops[k][0] !== "="; k++) (ops[k][0] === "-" ? dels : adds).push(ops[k][0] === "-" ? ops[k][1] : ops[k][2]);
    k--;
    // pair a removed block with an added one of the same kind: a changed paragraph, unless most of it is new
    let i = 0, j = 0;
    while (i < dels.length || j < adds.length) {
      const d = dels[i], a = adds[j];
      if (d && a && d.type === a.type && d.type !== "code" && d.type !== "hr") {
        const w = styledDiff(d.text, a.text);
        if (w.ratio >= 0.35) { keep(a, "mod", w.segs); i++; j++; continue; }
      }
      if (d && (!a || dels.length - i >= adds.length - j)) { keep(d, "del", plain(d, "del")); i++; }
      else { keep(a, "add", plain(a, "add")); j++; }
    }
  }
  return out;
}

export function changesOnly(blocks) {
  const shown = blocks.map((b) => b.change !== "same");
  const context = blocks.map(() => false);
  blocks.forEach((b, i) => {
    if (b.change === "same" || /^h\d$/.test(b.type)) return;
    for (let j = i - 1; j >= 0; j--) if (/^h\d$/.test(blocks[j].type)) { if (!shown[j]) context[j] = true; break; }
  });
  const out = [];
  let gap = 0;
  blocks.forEach((b, i) => {
    if (shown[i] || context[i]) { if (gap) out.push({ gap }); gap = 0; out.push(context[i] ? { ...b, context: true } : b); }
    else gap++;
  });
  if (gap) out.push({ gap });
  return out;
}
